// Scenario loader: orchestrates the terrain, weather, and infrastructure
// providers into a ready-to-simulate scenario for any location + date range.

import { bboxFromCenter, gridFromBbox, latLonToCell, isInBounds } from "./geo";
import { fetchElevation, fetchWaterMask } from "./terrainService";
import { fetchWeatherTimeline, metarFallbackTimeline } from "./weatherService";
import { fetchInfrastructure } from "./infrastructureService";
import {
  STATE_TREE,
  STATE_WATER,
  STATE_BURNING,
  STATE_INFRASTRUCTURE,
} from "../simulation/constants";

export const DEFAULT_GRID_COLS = 110;

// Historical scenario presets. `ignitions` are real reported origin points.
export const PRESETS = [
  {
    id: "palisades",
    name: "Palisades Fire — Jan 2025 (Los Angeles, CA)",
    center: { lat: 34.06, lon: -118.55 },
    radiusKm: 8,
    startISO: "2025-01-07T18:00:00Z", // ~10:30 AM PST ignition
    endISO: "2025-01-16T00:00:00Z",
    ignitions: [{ lat: 34.0722, lon: -118.5443 }], // Skull Rock / Temescal ridge
    burnMapUrl: "/palisade_burn_map.png",
  },
  {
    id: "eaton",
    name: "Eaton Fire — Jan 2025 (Altadena, CA)",
    center: { lat: 34.185, lon: -118.1 },
    radiusKm: 7.5,
    startISO: "2025-01-08T02:00:00Z", // ~6:18 PM PST Jan 7 ignition
    endISO: "2025-01-16T00:00:00Z",
    ignitions: [{ lat: 34.1897, lon: -118.0308 }], // Eaton Canyon
  },
  {
    id: "marshall",
    name: "Marshall Fire — Dec 2021 (Boulder County, CO)",
    center: { lat: 39.955, lon: -105.185 },
    radiusKm: 7,
    startISO: "2021-12-30T18:00:00Z",
    endISO: "2022-01-02T00:00:00Z",
    ignitions: [{ lat: 39.9556, lon: -105.2325 }], // Marshall Mesa
  },
];

// Deterministic smooth value-noise for fuel heterogeneity
const makeFuelNoise = (rows, cols, seed = 1337) => {
  let s = seed;
  const rand = () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
  let noise = Array(rows)
    .fill(null)
    .map(() => Array.from({ length: cols }, () => rand()));
  // Two smoothing passes -> coherent patches instead of white noise
  for (let pass = 0; pass < 2; pass++) {
    const next = Array(rows)
      .fill(null)
      .map(() => new Array(cols));
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        let sum = 0,
          n = 0;
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            const nr = r + dr,
              nc = c + dc;
            if (nr >= 0 && nr < rows && nc >= 0 && nc < cols) {
              sum += noise[nr][nc];
              n++;
            }
          }
        }
        next[r][c] = sum / n;
      }
    }
    noise = next;
  }
  return noise;
};

// Percentile-normalized 0-255 elevation for rendering, plus hillshade factors
const buildRenderTerrain = (elevation, water, rows, cols, cellSizeM) => {
  const landElevs = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (!water[r][c]) landElevs.push(elevation[r][c]);
    }
  }
  landElevs.sort((a, b) => a - b);
  const p = (q) => landElevs[Math.floor(q * (landElevs.length - 1))] ?? 0;
  const lo = p(0.02);
  const hi = Math.max(lo + 1, p(0.98));

  const renderElevation = Array(rows)
    .fill(null)
    .map(() => new Float32Array(cols));
  const shade = Array(rows)
    .fill(null)
    .map(() => new Float32Array(cols));

  // Hillshade: sun from the northwest at 45° altitude
  const azRad = (315 * Math.PI) / 180;
  const altRad = (45 * Math.PI) / 180;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      renderElevation[r][c] = Math.max(
        0,
        Math.min(255, ((elevation[r][c] - lo) / (hi - lo)) * 255)
      );
      const rp = Math.max(0, r - 1),
        rn = Math.min(rows - 1, r + 1);
      const cp = Math.max(0, c - 1),
        cn = Math.min(cols - 1, c + 1);
      const dzdx = (elevation[r][cn] - elevation[r][cp]) / (2 * cellSizeM);
      const dzdy = (elevation[rn][c] - elevation[rp][c]) / (2 * cellSizeM);
      const slopeRad = Math.atan(Math.hypot(dzdx, dzdy));
      const aspectRad = Math.atan2(dzdy, -dzdx);
      const hs =
        Math.sin(altRad) * Math.cos(slopeRad) +
        Math.cos(altRad) * Math.sin(slopeRad) * Math.cos(azRad - aspectRad);
      shade[r][c] = 0.65 + Math.max(0, Math.min(1, hs)) * 0.5; // 0.65..1.15
    }
  }
  return { renderElevation, shade };
};

// Find nearest non-water cell to (row, col) within `maxRing` rings
const nearestLandCell = (row, col, water, rows, cols, maxRing = 5) => {
  if (isInBounds(row, col, rows, cols) && !water[row][col]) return { row, col };
  for (let ring = 1; ring <= maxRing; ring++) {
    for (let dr = -ring; dr <= ring; dr++) {
      for (let dc = -ring; dc <= ring; dc++) {
        if (Math.max(Math.abs(dr), Math.abs(dc)) !== ring) continue;
        const nr = row + dr,
          nc = col + dc;
        if (isInBounds(nr, nc, rows, cols) && !water[nr][nc])
          return { row: nr, col: nc };
      }
    }
  }
  return null;
};

// --- Local cache for derived geo grids ----------------------------------
// Terrain + infrastructure grids are deterministic per region and small once
// aggregated to the sim grid (~250 KB), so cache them in localStorage. This
// makes reloads instant and shields against Overpass server load.
const GEO_CACHE_VERSION = 1;

const geoCacheKey = (config, cols) =>
  `wfs-geo-v${GEO_CACHE_VERSION}:${config.center.lat.toFixed(4)},${config.center.lon.toFixed(4)},${config.radiusKm},${cols}`;

const readGeoCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return {
      elevation: data.elevation.map((row) => Float32Array.from(row)),
      water: data.water.map((row) => Uint8Array.from(row)),
      structureCount: data.structureCount.map((row) => Uint16Array.from(row)),
      structureValue: data.structureValue.map((row) => Float32Array.from(row)),
      totalStructures: data.totalStructures,
      totalValue: data.totalValue,
      sources: data.sources,
    };
  } catch {
    return null;
  }
};

const writeGeoCache = (key, geo) => {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        elevation: geo.elevation.map((row) =>
          Array.from(row, (v) => Math.round(v * 10) / 10)
        ),
        water: geo.water.map((row) => Array.from(row)),
        structureCount: geo.structureCount.map((row) => Array.from(row)),
        structureValue: geo.structureValue.map((row) =>
          Array.from(row, (v) => Math.round(v * 100) / 100)
        ),
        totalStructures: geo.totalStructures,
        totalValue: geo.totalValue,
        sources: geo.sources,
      })
    );
  } catch (err) {
    console.warn("Geo cache write failed (quota?):", err);
  }
};

// config: { name, center: {lat, lon}, radiusKm, startDate, endDate,
//           ignitions: [{lat, lon}], gridCols?, burnMapUrl? }
//
// Loads progressively: `onPartial(scenario)` fires as soon as terrain +
// weather are in (map becomes usable in seconds), then again as each
// building quadrant streams in. The returned promise resolves with the
// complete scenario. Partial scenarios have `complete: false`.
export const loadScenario = async (config, onProgress, onPartial) => {
  const cols = config.gridCols || DEFAULT_GRID_COLS;
  const bbox = bboxFromCenter(config.center.lat, config.center.lon, config.radiusKm);
  const grid = gridFromBbox(bbox, cols);
  const dims = { rows: grid.rows, cols: grid.cols };
  const { rows } = dims;

  onProgress?.("Preparing scenario grid…");

  // Weather is always fetched fresh (depends on the date range)
  const weatherPromise = fetchWeatherTimeline(
    {
      lat: config.center.lat,
      lon: config.center.lon,
      startDate: config.startDate,
      endDate: config.endDate,
    },
    onProgress
  ).catch((err) => {
    console.warn("Weather fetch failed, using METAR fallback:", err);
    return metarFallbackTimeline();
  });

  // Derived grids are cheap (~12k cells), so each stage re-assembles the full
  // scenario from whatever data has arrived. Terrain shading and fuel noise
  // are computed once and reused across stages.
  const noise = makeFuelNoise(rows, dims.cols);
  let renderTerrain = null;

  const assemble = (elevation, water, infra, weather, sources, complete) => {
    if (!renderTerrain) {
      renderTerrain = buildRenderTerrain(
        elevation,
        water,
        rows,
        dims.cols,
        grid.cellSizeM
      );
    }

    // Fuel load (0..~1.3): wildland base with coherent spatial variation;
    // built-up cells carry less continuous vegetation fuel.
    const fuel = Array(rows)
      .fill(null)
      .map(() => new Float32Array(dims.cols));
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < dims.cols; c++) {
        if (water[r][c]) continue;
        let f = 0.75 + noise[r][c] * 0.55; // 0.75..1.3 wildland
        const count = infra?.structureCount[r][c] || 0;
        if (count > 0) f *= Math.max(0.35, 1 - 0.07 * count);
        fuel[r][c] = f;
      }
    }

    // Initial cell states
    const initialState = Array(rows)
      .fill(null)
      .map(() => new Array(dims.cols));
    const initialGridType = Array(rows)
      .fill(null)
      .map(() => new Array(dims.cols));
    let initialInfraCount = 0;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < dims.cols; c++) {
        let s;
        if (water[r][c]) s = STATE_WATER;
        else if ((infra?.structureCount[r][c] || 0) > 0) {
          s = STATE_INFRASTRUCTURE;
          initialInfraCount++;
        } else s = STATE_TREE;
        initialState[r][c] = s;
        initialGridType[r][c] = s;
      }
    }

    // Ignition points
    const ignitions = [];
    for (const ig of config.ignitions || [config.center]) {
      const cell = latLonToCell(bbox, dims, ig.lat, ig.lon);
      const land = nearestLandCell(cell.row, cell.col, water, rows, dims.cols);
      if (land) {
        ignitions.push(land);
        initialState[land.row][land.col] = STATE_BURNING;
        if (initialGridType[land.row][land.col] === STATE_INFRASTRUCTURE)
          initialInfraCount--;
        initialGridType[land.row][land.col] = STATE_TREE;
      }
    }

    return {
      name: config.name,
      bbox,
      dims,
      cellSizeM: grid.cellSizeM,
      elevation,
      water,
      renderElevation: renderTerrain.renderElevation,
      shade: renderTerrain.shade,
      fuel,
      structureCount: infra?.structureCount || null,
      structureValue: infra?.structureValue || null,
      totalStructures: infra?.totalStructures || 0,
      totalValue: infra?.totalValue || 0,
      initialState,
      initialGridType,
      initialInfraCount,
      ignitions,
      weather,
      startTime: config.startDate,
      endTime: config.endDate,
      burnMapUrl: config.burnMapUrl || null,
      complete,
      sources,
    };
  };

  // --- Cache hit: single-stage assembly ---------------------------------
  const cacheKey = geoCacheKey(config, cols);
  const cached = readGeoCache(cacheKey);
  if (cached) {
    onProgress?.("Using cached terrain & infrastructure for this region");
    const weather = await weatherPromise;
    return assemble(cached.elevation, cached.water, cached, weather, {
      terrain: cached.sources.terrain + " (cached)",
      weather: weather.source,
      infrastructure: cached.sources.infrastructure + " (cached)",
    }, true);
  }

  // --- Stage 1: elevation + weather → provisional scenario ---------------
  const terrain = await fetchElevation(bbox, dims, onProgress);
  const weather = await weatherPromise;

  // Quick water mask (sea level only) until OSM polygons arrive
  const quickWater = Array(rows)
    .fill(null)
    .map((_, r) => {
      const row = new Uint8Array(dims.cols);
      for (let c = 0; c < dims.cols; c++) {
        if (terrain.elevation[r][c] <= 0.5) row[c] = 1;
      }
      return row;
    });

  const stageSources = (infraLabel) => ({
    terrain: terrain.source,
    weather: weather.source,
    infrastructure: infraLabel,
  });
  onPartial?.(
    assemble(terrain.elevation, quickWater, null, weather,
      stageSources("loading…"), false)
  );

  // --- Stage 2: buildings (parallel quadrants, streamed) + OSM water -----
  // Water goes to a different Overpass mirror than the building quadrants.
  const waterPromise = fetchWaterMask(bbox, dims, terrain.elevation, onProgress, {
    endpointOffset: 2,
  });
  const infra = await fetchInfrastructure(bbox, dims, onProgress, (grids) => {
    onPartial?.(
      assemble(terrain.elevation, quickWater, grids, weather,
        stageSources(`loading… (${grids.totalStructures.toLocaleString()} so far)`), false)
    );
  });
  const water = await waterPromise;

  // Don't cache a partial dataset — a later load should retry missing regions
  if (!infra.failedRegions) {
    writeGeoCache(cacheKey, {
      elevation: terrain.elevation,
      water,
      structureCount: infra.structureCount,
      structureValue: infra.structureValue,
      totalStructures: infra.totalStructures,
      totalValue: infra.totalValue,
      sources: { terrain: terrain.source, infrastructure: infra.source },
    });
  }

  onProgress?.("Scenario ready");
  return assemble(terrain.elevation, water, infra, weather, {
    terrain: terrain.source,
    weather: weather.source,
    infrastructure: infra.source,
  }, true);
};

export const configFromPreset = (preset) => ({
  name: preset.name,
  center: preset.center,
  radiusKm: preset.radiusKm,
  startDate: new Date(preset.startISO),
  endDate: new Date(preset.endISO),
  ignitions: preset.ignitions,
  burnMapUrl: preset.burnMapUrl,
});
